import { useMutation, useQueryClient } from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysUser } from '../queryKeys';

import {
  type UpdateUserSettingsAdminMutationOptions,
  type UpdateUserSettingsAdminVariables,
} from './types';
import { updateUserSettingsAdmin } from './updateUserSettingsAdmin';

/**
 * Custom hook for updating the settings stored on a user. Requires VESTIGAS admin write
 * access. Refetches the user's settings on settle, and resolves only after that refetch,
 * so a caller awaiting `mutateAsync` sees the stored state.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/update_user_settings_vadmin_user__user_id__settings_post
 */
export const useMutationUpdateUserSettingsAdmin = (
  options?: UpdateUserSettingsAdminMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ payload, userId }: UpdateUserSettingsAdminVariables) =>
      updateUserSettingsAdmin(userId, payload),
    ...options,
    async onSettled(...args) {
      const [, , variables] = args;

      await queryClient.invalidateQueries({
        queryKey: queryKeysUser.getSettingsAdmin(variables.userId),
      });

      await options?.onSettled?.(...args);
    },
    async onSuccess(...args) {
      ToastService.success(
        i18n.t(($) => $.user.updateUserSettingsAdmin.success, { ns: 'data' }),
      );

      await options?.onSuccess?.(...args);
    },
  });
};
